import React from "react";
import currencyCodes from "currency-codes";
import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  VStack,
  HStack,
  RadioGroup,
  Radio,
  Stack,
  Icon,
  Divider,
  Button,
  Checkbox,
} from "@chakra-ui/react";
import { FaCog, FaDatabase, FaCheckCircle } from "react-icons/fa";
import { ColumnInfo, FilterProto, Header, ObservationColumnProto } from "build-ts/metadata";
import {
  AggregationType,
  FilterOperator,
  MetricType,
  PolarityEnum,
  polarityEnumToJSON,
} from "build-ts/enums";
import ObservationComponent from "../../containers/configuration/ObservationContainer";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";

interface EditMetricDefinitionProps {
  metricName: string;
  metricDescription: string;
  selectedObservationColumn: Header;
  columns: ColumnInfo[];
  selectedAggregation: AggregationType | undefined;
  selectedObservationColumns: ObservationColumnProto[];
  selectedFilterColumn: ColumnInfo;
  selectedOperator: FilterOperator | undefined;
  inputValue: string;
  selectedFilterColumns: FilterProto[];
  selectedMateriality: Header | null;
  metricHeaders: Header[];
  decimalPlaces: number | null;
  polarity: PolarityEnum | undefined;
  metricType: MetricType | undefined;
  currency: string | undefined;
  suffix: string | undefined;
  hasContent: boolean;
  displayType: string;
  columnNames: string[];
  selectedColumnValues: string[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  createMetricFlag: boolean;

  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteObservationColumn: (indexToDelete: number) => void;
  handleDeleteFilterColumn: (indexToDelete: number) => void;
  submit: () => void;
  handlePolarityChange: (value: string) => void;
  handleMetricTypeChange: (isRatio: boolean) => void;
  handleCurrencyChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePercentageChange: (value: boolean) => void;
  handleSuffixChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToggle: (value: string) => void;
  handleMaterialColumn: (event: React.ChangeEvent<HTMLSelectElement>) => void;

  setDisplayType: React.Dispatch<React.SetStateAction<string>>;
  setDecimalPlaces: React.Dispatch<React.SetStateAction<number | null>>;
  setColumnNames: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedObservationColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  setSelectedAggregation: React.Dispatch<React.SetStateAction<AggregationType | undefined>>;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  setSelectedObservationColumns: React.Dispatch<React.SetStateAction<ObservationColumnProto[]>>;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;

  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}
const EditMetricDefinition: React.FC<EditMetricDefinitionProps> = ({
  metricName,
  metricDescription,
  selectedObservationColumn,
  columns,
  selectedAggregation,
  selectedFilterColumn,
  selectedObservationColumns,
  selectedFilterColumns,
  selectedOperator,
  inputValue,
  displayType,
  columnNames,
  selectedColumnValues,
  selectedMateriality,
  metricHeaders,
  decimalPlaces,
  polarity,
  metricType,
  currency,
  suffix,
  dateFilter,
  dateFilterCategory,
  createMetricFlag,

  handleNameChange,
  handleDescriptionChange,
  submit,
  handlePolarityChange,
  handleMetricTypeChange,
  handleCurrencyChange,
  handlePercentageChange,
  handleSuffixChange,
  handleMaterialColumn,

  setDisplayType,
  setDecimalPlaces,
  setSelectedObservationColumn,
  setColumnNames,
  setSelectedFilterColumn,
  setSelectedColumnValues,
  setSelectedAggregation,
  setSelectedOperator,
  setInputValue,
  setSelectedFilterColumns,
  setSelectedObservationColumns,
  setDateFilter,
  setDateFilterCategory,
  isLoading,
  setIsLoading
}) => {

  return (
    <Container maxW="container.xl" overflowY="auto" py={2}>
      <VStack spacing={8} align="stretch">
        {/* Metric Information Section */}
        <Box
          border="2px"
          borderColor="purple.700"
          borderRadius="xl"
          bg="white"
          boxShadow="lg"
        >
          {/* Header */}
          <HStack spacing={3} p={2} bg="purple.700" borderTopRadius="md">
            <Icon as={FaCog} boxSize={6} color="white" />
            <Text fontSize="lg" fontWeight="700" mb={0} color="white">
              Metric Properties
            </Text>
          </HStack>

          <VStack spacing={0} p={4}>
            {/* Name and Description */}
            <HStack spacing={4} width="100%">
              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Name</FormLabel>
                <Input
                  type="text"
                  placeholder="Name of metric"
                  value={metricName}
                  onChange={handleNameChange}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                />
              </FormControl>
              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Description</FormLabel>
                <Input
                  type="text"
                  placeholder="Describe the metric"
                  value={metricDescription}
                  onChange={handleDescriptionChange}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                />
              </FormControl>
            </HStack>

            <Divider />

            {/* Units and Polarity Row */}
            <HStack spacing={6} width="100%" align="flex-start">
              {/* Units Section */}
              <Box flex="1">
                <Text fontSize="sm" mb={3} fontWeight="600" color="gray.700">Units</Text>
                <RadioGroup colorScheme="purple" onChange={setDisplayType} value={displayType}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="Regular" bg="white" onClick={() => handlePercentageChange(false)}>
                      <Text fontSize="sm" fontWeight="500" mb={0}>Regular</Text>
                    </Radio>
                    <Radio value="Currency" bg="white" onClick={() => handlePercentageChange(false)}>
                      <Text fontSize="sm" fontWeight="500" mb={0}>Currency</Text>
                    </Radio>
                    <Radio value="Percentage" bg="white" onClick={() => handlePercentageChange(true)}>
                      <Text fontSize="sm" fontWeight="500" mb={0}>Percentage</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>

                {displayType === "Currency" && (
                  <Box mt={2}>
                    <Select
                      placeholder="Select Currency"
                      value={currency}
                      onChange={handleCurrencyChange}
                      bg="white"
                      fontSize="sm"
                      borderColor="purple.700"
                      _focus={{ borderColor: "purple.700", bg: "white" }}
                    >
                      {currencyCodes.data.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.currency}
                        </option>
                      ))}
                    </Select>
                  </Box>
                )}

                {displayType === "Regular" && (
                  <Input
                    type="text"
                    placeholder="Suffix (if any)"
                    value={suffix}
                    onChange={handleSuffixChange}
                    mt={2}
                    bg="white"
                    fontSize="sm"
                    borderColor="purple.700"
                    _focus={{ borderColor: "purple.700", bg: "white" }}
                  />
                )}
              </Box>

              {/* Polarity Section */}
              <VStack align="flex-start" spacing={8} flex="1">
                <Box>
                  <Text fontSize="sm" mb={3} fontWeight="600" color="gray.700">Polarity</Text>
                  <RadioGroup value={polarityEnumToJSON(polarity!)} colorScheme="purple">
                    <Stack direction="row" spacing={4}>
                      <Radio bg="white" value="POSITIVE" onClick={() => handlePolarityChange("POSITIVE")}>
                        <Text fontSize="sm" fontWeight="500" mb={0}>Positive</Text>
                      </Radio>
                      <Radio bg="white" value="NEGATIVE" onClick={() => handlePolarityChange("NEGATIVE")}>
                        <Text fontSize="sm" fontWeight="500" mb={0}>Negative</Text>
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </Box>
                <Checkbox
                  colorScheme="purple"
                  size="lg"
                  pt={7}
                  isChecked={metricType === MetricType.RATIO}
                  onChange={(e) => handleMetricTypeChange(e.target.checked)}
                >
                  <Text fontSize="sm" fontWeight="600" mb={0}>Ratio Metric</Text>
                </Checkbox>
              </VStack>
            </HStack>

            <Divider />

            {/* Material Metric and Precision Row */}
            <HStack spacing={4} width="100%">
              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Importance Depends On </FormLabel>
                <Select
                  placeholder="Select Metric"
                  onChange={handleMaterialColumn}
                  value={selectedMateriality?.id || ""}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                >
                  {metricHeaders && metricHeaders.map((metric) => (
                    <option key={metric.id} value={metric.id}>
                      {metric.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Decimal Precision</FormLabel>
                <Input
                  type="number"
                  placeholder="Decimal Places"
                  value={decimalPlaces!}
                  onChange={(e) => setDecimalPlaces(e.target.value ? Number(e.target.value) : null)}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                />
              </FormControl>
            </HStack>
          </VStack>

        </Box>
        <Box
          border="2px"
          borderColor="green.600"
          borderRadius="xl"
          bg="white"
          boxShadow="lg"
        >
          {/* Header */}
          <HStack spacing={3} p={2} bg="green.600" borderTopRadius="md">
            <Icon as={FaDatabase} boxSize={6} color="white" />
            <Text fontSize="lg" fontWeight="700" mb={0} color="white">
              Filters
            </Text>
          </HStack>

          <Box p={6}>
            <ObservationComponent
              type="filter"
              columns={columns}
              columnNames={columnNames}
              selectedObservationColumn={selectedObservationColumn}
              selectedFilterColumn={selectedFilterColumn}
              selectedAggregation={selectedAggregation}
              selectedOperator={selectedOperator}
              inputValue={inputValue}
              selectedColumnValues={selectedColumnValues}
              selectedFilterColumns={selectedFilterColumns}
              selectedObservationColumns={selectedObservationColumns}
              dateFilter={dateFilter}
              dateFilterCategory={dateFilterCategory}
              isLoading={isLoading}
              setSelectedObservationColumn={setSelectedObservationColumn}
              setColumnNames={setColumnNames}
              setSelectedFilterColumn={setSelectedFilterColumn}
              setSelectedColumnValues={setSelectedColumnValues}
              setSelectedAggregation={setSelectedAggregation}
              setSelectedOperator={setSelectedOperator}
              setInputValue={setInputValue}
              setSelectedFilterColumns={setSelectedFilterColumns}
              setSelectedObservationColumns={setSelectedObservationColumns}
              setDateFilter={setDateFilter}
              setDateFilterCategory={setDateFilterCategory}
              setIsLoading={setIsLoading}
            />
          </Box>
        </Box>


        {/* Submit Button */}
        {createMetricFlag && (
          <Box bg="white" mb={5}>
            <Button
              colorScheme="green"
              onClick={submit}
              w="100%"
              fontWeight="bold"
              fontSize="lg"
              leftIcon={<FaCheckCircle />}
            >
              Save
            </Button>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default EditMetricDefinition;